import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  getAllOrderCountAndSalesBrandOwner,
  getBrandOwnerOrders,
} from '../../../api/brandOwner/orders.brandOwner-api';
import { getAllOrderCountAndSales, getAllOrders } from '../../../api/order.api';
import type { DataHistory } from '../../../store/orders';
import type {
  GetAllOrdersCountAndSales,
  GetAllOrdersParams,
} from '../../../types/api/order.types';
import type {
  AdvancedBrandTypeQuery,
  GetAllOrdersBrandOwnerParams,
  GetAllOrdersCountAndSalesBrandOwner,
} from '../../../types/brandOwner/order.brandOwner-types';
import type { SimpleLocationEntity } from '../../../types/location.types';
import { SortByDate, LocationStatus } from '../../../types/shared.types';
import { queryKeys } from '../../../api/queryKeys';

const PAGE_SIZE = 30;

/** Root margin (px) below viewport - fetch next page when sentinel is this far from bottom */
const PRELOAD_ROOT_MARGIN = '1000px';

const parseIds = (param: string | null): number[] =>
  param
    ? param
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !Number.isNaN(id))
    : [];

export type OrdersFilters = {
  query: string | null;
  from: string;
  to: string;
  status: LocationStatus;
  selectedLocation: SimpleLocationEntity | null;
  advancedQueryType?: string;
  locationIdsParam: string | null;
  brandIdsParam: string | null;
  isAdmin: boolean;
  isBrandOwner: boolean;
  isPartner: boolean;
};

function buildOrdersParams(
  filters: OrdersFilters,
  page: number
): GetAllOrdersParams | GetAllOrdersBrandOwnerParams {
  const {
    from,
    to,
    status,
    query,
    selectedLocation,
    advancedQueryType,
    locationIdsParam,
    brandIdsParam,
    isAdmin,
    isPartner,
  } = filters;

  const base = {
    from,
    to,
    page,
    limit: PAGE_SIZE,
    ordersFind: status,
    sortByDate: SortByDate.DESC,
    query: query || null,
  };

  if (filters.isBrandOwner) {
    const params: GetAllOrdersBrandOwnerParams = {
      ...base,
      type: (advancedQueryType ?? 'all') as AdvancedBrandTypeQuery,
    };
    if (status === LocationStatus.ACTIVE) params.sortByStatus = SortByDate.ASC;
    return params;
  }

  const params: GetAllOrdersParams = { ...base };
  if (status === LocationStatus.ACTIVE) params.sortByStatus = SortByDate.ASC;

  if (isAdmin) {
    const locationIds = parseIds(locationIdsParam);
    const brandIds = parseIds(brandIdsParam);
    if (locationIds.length) params.locationIds = locationIds;
    if (brandIds.length) params.brandIds = brandIds;
  }

  if (isPartner && selectedLocation) {
    params.locationId = selectedLocation.id;
  }

  return params;
}

function buildCountAndSalesParams(
  filters: OrdersFilters
): GetAllOrdersCountAndSales | GetAllOrdersCountAndSalesBrandOwner {
  const {
    from,
    to,
    status,
    query,
    selectedLocation,
    advancedQueryType,
    locationIdsParam,
    brandIdsParam,
    isAdmin,
    isPartner,
  } = filters;

  const base = {
    from,
    to,
    ordersFind: status,
    sortByDate: SortByDate.DESC,
    query: query || null,
  };

  if (filters.isBrandOwner) {
    return {
      ...base,
      type: (advancedQueryType ?? 'all') as AdvancedBrandTypeQuery,
    };
  }

  const params: GetAllOrdersCountAndSales = { ...base };

  if (isAdmin) {
    const locationIds = parseIds(locationIdsParam);
    const brandIds = parseIds(brandIdsParam);
    if (locationIds.length) params.locationIds = locationIds;
    if (brandIds.length) params.brandIds = brandIds;
  }

  if (isPartner && selectedLocation) {
    params.locationId = selectedLocation.id;
  }

  return params;
}

async function fetchOrdersPage(filters: OrdersFilters, pageParam: number) {
  const params = buildOrdersParams(filters, pageParam);
  const api = filters.isBrandOwner
    ? getBrandOwnerOrders(params as GetAllOrdersBrandOwnerParams)
    : getAllOrders(params as GetAllOrdersParams);

  const { data } = await api;
  const items = data?.data ?? [];
  const nextPage = items.length === PAGE_SIZE ? pageParam + 1 : undefined;

  return { orders: items, nextPage };
}

const SCROLL_KEY = 'admin-orders-scroll';

function mergeOrdersIntoDataHistory(
  dateBuckets: DataHistory[],
  allOrders: DataHistory['orders'],
  isBrandOwner: boolean
): DataHistory[] {
  const ordersByDate: Record<string, DataHistory['orders']> = {};
  (allOrders ?? []).forEach((order) => {
    const date = DateTime.fromISO(order.date)
      .setZone(isBrandOwner ? undefined : order.timeZone)
      .toISODate();
    if (date) {
      if (!ordersByDate[date]) ordersByDate[date] = [];
      ordersByDate[date]!.push(order);
    }
  });

  return dateBuckets.map((day) => ({
    ...day,
    orders: ordersByDate[day.salesDay] ?? [],
  }));
}

export function useOrdersInfiniteScroll(filters: OrdersFilters) {
  const observerRef = useRef<HTMLDivElement | null>(null);
  const isHistoryOrCancelled =
    filters.status === LocationStatus.HISTORY ||
    filters.status === LocationStatus.CANCELLED;

  const enabled =
    (!filters.isPartner || filters.selectedLocation !== null) &&
    (!filters.isBrandOwner || !!filters.advancedQueryType);

  const ordersQuery = useInfiniteQuery({
    queryKey: [
      ...queryKeys.orders.all,
      filters.from,
      filters.to,
      filters.status,
      filters.advancedQueryType,
      filters.selectedLocation?.id,
      filters.locationIdsParam,
      filters.brandIdsParam,
      filters.query,
    ],
    queryFn: ({ pageParam }) => fetchOrdersPage(filters, pageParam),
    getNextPageParam: (last) => last.nextPage,
    initialPageParam: 1,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - keeps cache for scroll restoration
  });

  const countSalesQuery = useQuery({
    queryKey: [
      ...queryKeys.orders.countSalesAll,
      filters.from,
      filters.to,
      filters.status,
      filters.advancedQueryType,
      filters.selectedLocation?.id,
      filters.locationIdsParam,
      filters.brandIdsParam,
      filters.query,
    ],
    queryFn: async () => {
      const params = buildCountAndSalesParams(filters);
      const api = filters.isBrandOwner
        ? getAllOrderCountAndSalesBrandOwner(
            params as GetAllOrdersCountAndSalesBrandOwner
          )
        : getAllOrderCountAndSales(params as GetAllOrdersCountAndSales);
      const { data } = await api;
      return data?.data ?? [];
    },
    enabled: enabled && isHistoryOrCancelled,
  });

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const { hasNextPage, fetchNextPage, isFetchingNextPage, isFetching } =
      ordersQuery;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetching
        ) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    ordersQuery.hasNextPage,
    ordersQuery.fetchNextPage,
    ordersQuery.isFetchingNextPage,
    ordersQuery.isFetching,
  ]);

  const orders = useMemo(
    () => ordersQuery.data?.pages.flatMap((p) => p.orders) ?? [],
    [ordersQuery.data]
  );

  const dataHistory = useMemo(() => {
    const buckets = countSalesQuery.data ?? [];
    return isHistoryOrCancelled && buckets.length > 0
      ? mergeOrdersIntoDataHistory(buckets, orders, filters.isBrandOwner)
      : [];
  }, [
    isHistoryOrCancelled,
    countSalesQuery.data,
    orders,
    filters.isBrandOwner,
  ]);

  const saveScrollPosition = useCallback(() => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  }, []);

  return {
    orders,
    dataHistory,
    observerRef,
    isFetchingNextPage: ordersQuery.isFetchingNextPage,
    isFetching: ordersQuery.isFetching,
    status: ordersQuery.status,
    hasNextPage: ordersQuery.hasNextPage,
    refetch: ordersQuery.refetch,
    isCountSalesLoading: countSalesQuery.isFetching,
    saveScrollPosition,
  };
}
